import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, Star, X, ChevronRight, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useToolTheme } from '@/contexts/ThemeContext';

interface Update {
  id: string;
  title: string;
  description: string;
  date: Date;
  type: 'feature' | 'improvement' | 'fix';
  category: string;
  details: string[];
}

const UPDATES: Update[] = [
  {
    id: '2025-03-01',
    title: "Nouvelle version du Baromètre Énergétique",
    description: "De nouvelles fonctionnalités pour mieux suivre votre énergie",
    date: new Date('2025-03-01'),
    type: 'feature',
    category: 'Général',
    details: [
      "🎯 Nouveau système de notifications intelligentes",
      "💪 Mini-exercices pratiques personnalisés",
      "📊 Tableau de bord amélioré avec suivi sur 30 jours",
      "🏆 Système de gamification avec défis hebdomadaires",
      "🤖 Assistant IA pour un suivi personnalisé"
    ]
  },
  {
    id: '2025-02-15',
    title: "Amélioration des recommandations",
    description: "Des conseils plus précis selon votre type énergétique",
    date: new Date('2025-02-15'),
    type: 'improvement',
    category: 'Fonctionnalités',
    details: [
      "✨ Recommandations basées sur l'historique",
      "📝 Journal de bord énergétique",
      "🎯 Mode immersion avec notifications contextuelles",
      "🆘 Mode urgence pour les baisses d'énergie"
    ]
  }
];

export function UpdateCenter() {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState<Update | null>(null);
  const [hasNewUpdates, setHasNewUpdates] = useState(true);
  const { toolColors } = useToolTheme();

  useEffect(() => {
    const lastSeenUpdate = localStorage.getItem('lastSeenUpdate');
    if (lastSeenUpdate !== UPDATES[0].id) {
      setHasNewUpdates(true);
    }
  }, []);

  const markUpdateAsSeen = () => {
    localStorage.setItem('lastSeenUpdate', UPDATES[0].id);
    setHasNewUpdates(false);
  };

  const getUpdateTypeColor = (type: Update['type']) => {
    switch (type) {
      case 'feature':
        return 'bg-green-100 text-green-800';
      case 'improvement':
        return 'bg-blue-100 text-blue-800';
      case 'fix':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="relative text-gray-700 hover:text-gray-900"
        onClick={() => {
          setShowPopup(true);
          markUpdateAsSeen();
        }}
      >
        <Bell className="w-5 h-5" />
        {hasNewUpdates && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />
        )}
      </Button>

      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          >
            <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5" style={{ color: toolColors.primary }} />
                  <h2 className="text-xl font-bold">Nouveautés</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPopup(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-y-auto max-h-[60vh] pr-2">
                  {selectedUpdate ? (
                    <div className="space-y-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedUpdate(null)}
                        className="mb-4 text-gray-700"
                      >
                        ← Retour
                      </Button>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-500">
                            {format(selectedUpdate.date, 'dd MMMM yyyy', { locale: fr })}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold">{selectedUpdate.title}</h3>
                        <p className="text-gray-600">{selectedUpdate.description}</p>
                      </div>
                      <div className="space-y-3 mt-6">
                        {selectedUpdate.details.map((detail, index) => (
                          <div
                            key={index}
                            className="p-3 bg-purple-50 rounded-lg text-gray-800"
                          >
                            {detail}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {UPDATES.map((update) => (
                        <div
                          key={update.id}
                          className="p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setSelectedUpdate(update)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs ${getUpdateTypeColor(update.type)}`}>
                                {update.category}
                              </span>
                              <span className="text-sm text-gray-500">
                                {format(update.date, 'dd MMMM yyyy', { locale: fr })}
                              </span>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </div>
                          <h3 className="font-semibold mb-1 text-gray-900">{update.title}</h3>
                          <p className="text-sm text-gray-600">{update.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}